import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Checkbox } from 'blaze-ui/Checkbox/Checkbox'
import { FileRejection, useDropzone } from 'react-dropzone'
import cx from 'classnames'
import { debounce } from 'lodash'
import { generatePath, useHistory } from 'react-router-dom'

import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import Styles from './ImportModal.module.scss'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { SourceType } from './Types'
import { FOLDER_DOC_PATH } from 'paths'
import { createNewArticle, getArticleLink } from 'utils'
import { TextInputField } from 'components/Form/TextInputField'
import { FileType, LOADING_TITLE } from 'components/ReviewsWizard/FileSelection/urlValidationUtils'
import { InstructionalScreenshot } from 'components/ReviewsWizard/FileSelection/InstructionalScreenshot'
import { EmptyPreview } from 'components/ReviewsWizard/FileSelection/EmptyPreview'
import { useValidateFileUrl } from 'components/ReviewsWizard/FileSelection/useValidateFileUrl'
import { ExternalResourceRenderer } from 'screens/ProseEdit/components/ExternalResourceRenderer/ExternalResourceRenderer'

import { ReactComponent as ConfluenceIcon } from './icons/confluence.svg'
import { ReactComponent as NotionIcon } from './icons/notion.svg'
import { UploadCloud as CloudIcon } from 'almanac-ui/icons/16/UploadCloud'
import { ReactComponent as GoogleDocsIcon } from './icons/gdocs.svg'
import { ReactComponent as GoogleSlidesIcon } from './icons/GoogleSlides.svg'
import { ReactComponent as GoogleSheetsIcon } from './icons/GoogleSheets.svg'
import { ReactComponent as CanvaIcon } from './icons/Canva.svg'
import { ReactComponent as FigmaIcon } from './icons/Figma.svg'
import { ReactComponent as AirtableIcon } from './icons/Airtable.svg'
import { ReactComponent as ArrowUpIcon } from 'assets/icons/arrowUp16.svg'
import { ReactComponent as RightArrowIcon } from 'assets/icons/arrowRight16.svg'
import { ReactComponent as NameIcon } from 'common/PropertyItem/icons/text__16.svg'
import { ReactComponent as ImportIcon } from 'assets/icons/import24.svg'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar16.svg'
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg'
import { defaultQueryString } from './utils'

import confluenceIndividualImage from './images/confluence_individual.png'

import { Button } from 'components/Button/Button'
import { useGoogleDrive } from 'hooks/useGoogleDrive'
import ImportFileRow, { GoogleDriveFile } from './ImportFileRow'
import { convertBytesToFile } from './utils'
import { EmptyState, LoadingState } from 'common/GoogleIntegration/GoogleStates'
import QueryString from 'qs'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { useQueryParams } from 'hooks/useQueryParams'
import { Modal } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface SourceItemProp {
  name: string
  icon: any
  disabled?: boolean
  onClick?: () => void
}

const uploadOptions = ['DOCX', 'Markdown', 'HTML']

const SourceItem = ({ name, icon, disabled = false, onClick }: SourceItemProp) => {
  return (
    <button className={Styles.sourceCol} disabled={disabled} onClick={onClick} type="button">
      <span className={Styles.sourceIcon}> {icon} </span>
      <span> {name} </span>
    </button>
  )
}

const ASSET_TITLE_ERROR = 'You must enter a name for the file'

interface SourceProp {
  onSourceSelected?: (source: SourceType) => void
  filesSelected: (files: File[]) => void
  startReview?: boolean
  onGoBack?: () => void
}

const DraggingScreen = () => {
  return (
    <div className={Styles.draggingContent}>
      <div className={Styles.draggingIcon}>
        <ImportIcon />
      </div>
      <div>Drag here to upload</div>
    </div>
  )
}

const DragDefaultScreen = ({ isMain, onPress, icon }: { icon?: ReactNode; isMain: boolean; onPress: () => void }) => {
  return (
    <div className={Styles.dragDefaultScreen}>
      {!isMain && (
        <div className={Styles.optionChipContainer}>
          <div className={Styles.optionChip}>{icon}</div>
        </div>
      )}
      <div className={Styles.dropZoneText}>{isMain ? 'Drag & drop files' : 'Drop the files you exported here, or'}</div>
      {isMain && (
        <div className={Styles.optionChipContainer}>
          {uploadOptions.map((option, index) => (
            <div className={Styles.optionChip} key={`option-${index}`}>
              {option}
            </div>
          ))}
        </div>
      )}
      <Button className={Styles.dropZoneButton} frontIcon={CloudIcon} size="lg" onPress={onPress}>
        Upload from Your Device
      </Button>
    </div>
  )
}

export const Main = ({ onSourceSelected, filesSelected }: SourceProp) => {
  const onDrop = useCallback(filesSelected, [filesSelected])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: '.html,.md,.docx,.zip',
    onDropRejected: (fileRejections: FileRejection[]) => {
      const rejectedFiles = fileRejections.map((r) => r.file.name).join(', ')
      showErrorMessageToast(`We do not support the following files: ${rejectedFiles}`)
    },
  })
  const inputFile = useRef<HTMLInputElement>(null)

  return (
    <Modal.Content {...getRootProps()}>
      <div
        className={cx({
          [Styles.dropZone2]: true,
          [Styles.dropZone2Active]: isDragActive,
          [Styles.isMain]: true,
        })}
      >
        <input {...getInputProps()} ref={inputFile} accept=".html,.md,.docx,.zip" />
        {isDragActive ? <DraggingScreen /> : <DragDefaultScreen isMain onPress={() => inputFile.current?.click()} />}
      </div>
      {!isDragActive && (
        <>
          <div className={Styles.learnText2}>Upload from other services</div>
          <div className={Styles.sourceContainer}>
            <SourceItem
              name="Google Docs"
              icon={<GoogleDocsIcon />}
              onClick={() => onSourceSelected && onSourceSelected(SourceType.GoogleDocs)}
            />
            <SourceItem
              name="Google Sheets"
              icon={<GoogleSheetsIcon />}
              onClick={() => onSourceSelected && onSourceSelected(SourceType.GoogleSheets)}
            />
            <SourceItem
              name="Google Slides"
              icon={<GoogleSlidesIcon />}
              onClick={() => onSourceSelected && onSourceSelected(SourceType.GoogleSlides)}
            />
            <SourceItem
              name="Notion"
              icon={<NotionIcon />}
              disabled={false}
              onClick={() => onSourceSelected && onSourceSelected(SourceType.Notion)}
            />
            <SourceItem
              name="Confluence"
              icon={<ConfluenceIcon />}
              onClick={() => onSourceSelected && onSourceSelected(SourceType.Confluence)}
            />
            <SourceItem
              name="Airtable"
              icon={<AirtableIcon />}
              onClick={() => onSourceSelected && onSourceSelected(SourceType.Airtable)}
            />
            <SourceItem
              name="Canva"
              icon={<CanvaIcon />}
              onClick={() => onSourceSelected && onSourceSelected(SourceType.Canva)}
            />
            <SourceItem
              name="Figma"
              icon={<FigmaIcon />}
              onClick={() => onSourceSelected && onSourceSelected(SourceType.Figma)}
            />
          </div>
        </>
      )}
    </Modal.Content>
  )
}

export const Notion = ({ filesSelected }: SourceProp) => {
  const onDrop = useCallback(filesSelected, [filesSelected])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: '.html,.md,.docx,.zip',
    onDropRejected: (fileRejections: FileRejection[]) => {
      const rejectedFiles = fileRejections.map((r) => r.file.name).join(', ')
      showErrorMessageToast(`We do not support the following files: ${rejectedFiles}`)
    },
  })
  const inputFile = useRef<HTMLInputElement>(null)

  return (
    <Modal.Content className={cx({ [Styles.height600]: !isDragActive })} {...getRootProps()}>
      <div
        className={cx({
          [Styles.dropZone2]: true,
          [Styles.dropZone2Active]: isDragActive,
        })}
        onClick={() => inputFile.current?.click()}
      >
        <input {...getInputProps()} ref={inputFile} accept=".html,.md,.docx,.zip" />
        {isDragActive ? (
          <DraggingScreen />
        ) : (
          <DragDefaultScreen isMain={false} onPress={() => inputFile.current?.click()} icon={<NotionIcon />} />
        )}
      </div>
      {!isDragActive && (
        <div className={Styles.columnContainer}>
          <div className={Styles.column}>
            <div className={Styles.numberSection}>
              <div className={Styles.numberHeader}>Individual Notion documents</div>

              <div className={Styles.numberRow}>
                <div className={Styles.number}>1</div>
                <div>In Notion, select the More “•••” icon at the top right of the screen and click “Export”</div>
              </div>
              <div className={Styles.numberRow}>
                <div className={Styles.number}>2</div>
                <div>Export as HMTL or Markdown</div>
              </div>
            </div>

            <div className={Styles.numberSection}>
              <div className={Styles.numberHeader}>Multiple Notion documents</div>

              <div className={Styles.numberRow}>
                <div className={Styles.number}>1</div>
                <div>In Notion, click “Settings & Members” in the left-hand menu and open Settings. </div>
              </div>
              <div className={Styles.numberRow}>
                <div className={Styles.number}>2</div>
                <div>Scroll down to Export, and click “Export all workspace content.”</div>
              </div>
              <div className={Styles.numberRow}>
                <div className={Styles.number}>3</div>
                <div>Choose “HTML” and click “Export.”</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal.Content>
  )
}

const ImportViaLink = observer(
  ({
    chosenFrontIcon,
    chosenFileType,
    onClose,
    startReview,
    parentId,
    defaultEmbedLink,
    parentSlug,
  }: {
    defaultEmbedLink?: string
    chosenFrontIcon: React.ReactElement
    chosenFileType: FileType
    onClose: () => void
    startReview: boolean
    parentId: number | undefined
    parentSlug: string | undefined
  }) => {
    const workspace = useCurrentWorkspace()
    const history = useHistory()
    const { focusView } = useQueryParams()
    const handbooks = useRootStore().handbooks
    const [externalLink, setExternalLink] = useState(defaultEmbedLink || '')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const [title, setTitle] = useState('')
    const [currentExternalIcon, setCurrentExternalIcon] = useState<FileType>()
    const onTitleChange = (fileTitle: string) => {
      setTitle(fileTitle)
    }
    const onIconChange = (type: FileType) => {
      setCurrentExternalIcon(type)
    }
    const { validateUrl, isEmbeddable, fileType, isValid, frontIcon } = useValidateFileUrl({
      onTitleChange,
      onIconChange,
    })
    const [currentFileType, setCurrentFileType] = useState(chosenFileType)
    const [currentFrontIcon, setCurrentFrontIcon] = useState<ReactNode>(chosenFrontIcon)
    const externalLinkValid = externalLink && externalLink.length > 0 && isValid
    const showAssetTitleError = title.length < 1 && title === LOADING_TITLE

    useEffect(() => {
      if (!!fileType) {
        setCurrentFileType(fileType)
      } else {
        setCurrentFileType(chosenFileType)
      }
    }, [fileType, chosenFileType])

    useEffect(() => {
      if (!!frontIcon) {
        setCurrentFrontIcon(frontIcon)
      } else {
        setCurrentFrontIcon(chosenFrontIcon)
      }
    }, [frontIcon, chosenFrontIcon])

    const handleExternalLinkChange = debounce((value: string) => {
      const validUrl = validateUrl(value)
      setExternalLink(validUrl || value)
    }, 250)

    const handleAssetTitleChange = (value: string) => {
      setTitle(value)
    }

    const createArticle = async () => {
      if (!workspace) return

      setIsSubmitting(true)
      const res = await createNewArticle({
        workspaceKey: workspace?.key,
        projectId: parentId,
        title,
        externalLink,
        externalFileIcon: currentExternalIcon,
      })

      setIsSubmitting(false)
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        const article = res.data.article
        // This is not great. I'm having to do this because the server doesn't
        // return the handbook key for a newly created article. We should
        // provide handbook summary for all the assets that also happen to be
        // handbook items to make client side refresh easier.
        const handbookKey = article.project?.seo_string?.split('-').reverse()[0]
        if (handbookKey) {
          handbooks.refresh(handbookKey)
        }
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-nested-ternary
        const pathname = startReview
          ? `${getArticleLink(article)}/reviews/new`
          : parentSlug
          ? generatePath(FOLDER_DOC_PATH, { folderSlug: parentSlug, doc: article.slug })
          : `${getArticleLink(article)}/reviews/new`

        history.push({
          pathname,
          search: QueryString.stringify(
            { focusView },
            {
              filter: (_, value) => value ?? undefined,
            },
          ),
        })
        onClose()
      } else {
        reportError(new Error('Error while creating a new document'), { metaData: { res } })
        showErrorMessageToast('Something went wrong 😢')
      }
    }

    return (
      <>
        <Modal.Content className={Styles.importViaLink}>
          <div className={Styles.body}>
            <div className={Styles.left}>
              <TextInputField
                autoFocus
                placeholder={'Paste an embeddable link here...'}
                label="Link or doc"
                aria-label="Select File"
                value={externalLink}
                onChange={(e) => handleExternalLinkChange(e.target.value)}
                size="lg"
              />
              {externalLinkValid && isEmbeddable && (
                <TextInputField
                  autoFocus
                  errorMessage={showAssetTitleError && ASSET_TITLE_ERROR}
                  label="File Name"
                  aria-label="File Name"
                  onChange={(e) => handleAssetTitleChange(e.target.value)}
                  value={title}
                  size="lg"
                  isDisabled={title === LOADING_TITLE}
                  frontIcon={currentFrontIcon}
                  ref={inputRef}
                />
              )}
              {(!externalLinkValid || !isEmbeddable) && <InstructionalScreenshot fileType={currentFileType} />}
            </div>
            <div className={Styles.right}>
              <div className={Styles.filePreviewContainer}>
                <label className={Styles.inputLabel}>Preview</label>
                {isEmbeddable || !externalLink ? (
                  <ExternalResourceRenderer
                    fileType={currentFileType}
                    customClass={Styles.filePreview}
                    icon={currentFrontIcon}
                    externalLink={externalLink}
                    title={''}
                    isPreview
                    showLoadingState
                    showEmptyState={!externalLink}
                  />
                ) : (
                  <EmptyPreview fileType={currentFileType} />
                )}
              </div>
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="right">
            <Modal.FooterButton onPress={createArticle} isDisabled={isSubmitting} size="lg" endIcon={RightArrowIcon}>
              Open File
            </Modal.FooterButton>
          </Modal.FooterContent>
        </Modal.Footer>
      </>
    )
  },
)

export const Airtable = ({
  onClose,
  startReview,
  parentId,
  parentSlug,
  defaultEmbedLink,
}: {
  defaultEmbedLink?: string
  onClose: () => void
  startReview: boolean
  parentId: number | undefined
  parentSlug: string | undefined
}) => {
  return (
    <ImportViaLink
      chosenFrontIcon={<AirtableIcon />}
      chosenFileType={FileType.AIRTABLE}
      onClose={onClose}
      defaultEmbedLink={defaultEmbedLink}
      startReview={startReview}
      parentId={parentId}
      parentSlug={parentSlug}
    />
  )
}

export const Canva = ({
  onClose,
  startReview,
  parentId,
  defaultEmbedLink,
  parentSlug,
}: {
  onClose: () => void
  startReview: boolean
  parentId: number | undefined
  defaultEmbedLink?: string
  parentSlug: string | undefined
}) => {
  return (
    <ImportViaLink
      chosenFrontIcon={<CanvaIcon />}
      chosenFileType={FileType.CANVA}
      defaultEmbedLink={defaultEmbedLink}
      onClose={onClose}
      parentSlug={parentSlug}
      startReview={startReview}
      parentId={parentId}
    />
  )
}

export const Figma = ({
  onClose,
  startReview,
  parentId,
  defaultEmbedLink,
  parentSlug,
}: {
  onClose: () => void
  startReview: boolean
  parentId: number | undefined
  defaultEmbedLink?: string
  parentSlug: string | undefined
}) => {
  return (
    <ImportViaLink
      chosenFrontIcon={<FigmaIcon />}
      chosenFileType={FileType.FIGMA}
      onClose={onClose}
      startReview={startReview}
      parentSlug={parentSlug}
      parentId={parentId}
      defaultEmbedLink={defaultEmbedLink}
    />
  )
}

export const GoogleSheets = ({
  onClose,
  startReview,
  parentId,
  defaultEmbedLink,
  parentSlug,
}: {
  onClose: () => void
  startReview: boolean
  parentId: number | undefined
  defaultEmbedLink?: string
  parentSlug: string | undefined
}) => {
  return (
    <ImportViaLink
      chosenFrontIcon={<GoogleSheetsIcon />}
      chosenFileType={FileType.SPREADSHEETS}
      defaultEmbedLink={defaultEmbedLink}
      onClose={onClose}
      parentSlug={parentSlug}
      startReview={startReview}
      parentId={parentId}
    />
  )
}

export const GoogleSlides = ({
  onClose,
  startReview,
  parentId,
  defaultEmbedLink,
  parentSlug,
}: {
  onClose: () => void
  startReview: boolean
  parentId: number | undefined
  defaultEmbedLink?: string
  parentSlug: string | undefined
}) => {
  return (
    <ImportViaLink
      chosenFrontIcon={<GoogleSlidesIcon />}
      chosenFileType={FileType.PRESENTATION}
      defaultEmbedLink={defaultEmbedLink}
      onClose={onClose}
      parentSlug={parentSlug}
      startReview={startReview}
      parentId={parentId}
    />
  )
}

export const Confluence = ({ filesSelected }: SourceProp) => {
  const onDrop = useCallback(filesSelected, [filesSelected])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: '.html,.md,.docx,.zip',
    onDropRejected: (fileRejections: FileRejection[]) => {
      const rejectedFiles = fileRejections.map((r) => r.file.name).join(', ')
      showErrorMessageToast(`We do not support the following files: ${rejectedFiles}`)
    },
  })
  const inputFile = useRef<HTMLInputElement>(null)

  return (
    <Modal.Content {...getRootProps()}>
      <div
        className={cx({
          [Styles.dropZone2]: true,
          [Styles.dropZone2Active]: isDragActive,
        })}
        onClick={() => inputFile.current?.click()}
      >
        <input {...getInputProps()} ref={inputFile} accept=".html,.md,.docx,.zip" />
        {isDragActive ? (
          <DraggingScreen />
        ) : (
          <DragDefaultScreen isMain={false} onPress={() => inputFile.current?.click()} icon={<ConfluenceIcon />} />
        )}
      </div>
      {!isDragActive && (
        <div className={Styles.columnContainer}>
          <div className={Styles.column}>
            <div className={Styles.numberSection}>
              <div className={Styles.numberHeader}>Multiple Confluence documents</div>

              <div className={Styles.numberRow}>
                <div className={Styles.number}>1</div>
                <div>In Confluence, select the workspace you wish to export</div>
              </div>
              <div className={Styles.numberRow}>
                <div className={Styles.number}>2</div>
                <div>Go to Space Settings &#8594; Manage space &#8594; Export Space</div>
              </div>

              <div className={Styles.numberRow}>
                <div className={Styles.number}>3</div>
                <div>Choose “HTML” and click “Next”</div>
              </div>
              <div className={Styles.numberRow}>
                <div className={Styles.number}>4</div>
                <div>Choose “Normal Export” and click “Export”</div>
              </div>
            </div>
          </div>
          <div className={Styles.column}>
            <img
              src={confluenceIndividualImage}
              alt="Confluence screenshot"
              className={cx(Styles.screenshots, Styles.sized)}
            />
            <div className={Styles.screenshotHelpText}>Confluence Screenshot</div>
          </div>
        </div>
      )}
    </Modal.Content>
  )
}

export const GoogleDocs = ({ filesSelected, startReview, onGoBack }: SourceProp) => {
  const [selectedFiles, setSelectedFiles] = useState<GoogleDriveFile[]>([])
  const [isImporting, setIsImporting] = useState<Boolean>(false)
  const [searchFocused, setSearchFocused] = useState(false)
  const searchTerm = useRef<HTMLInputElement | null>(null)
  const [reload, setReload] = useState(true)
  const [searchAgain, setSearchAgain] = useState(false)
  const [queryString, setQueryString] = useState<string>(defaultQueryString)
  const { isMobile } = useResponsiveDesign()
  const { filesLoaded, email, files } = useGoogleDrive({ reload, searchAgain, queryString, selectedFiles })

  useEffect(() => {
    trackEventInHeap(HeapEventType.ImportDocs, { action: 'google_docs_import_started', type: '' })
  }, [])

  useEffect(() => {
    if (filesLoaded) {
      setReload(false)

      if (queryString === defaultQueryString) {
        trackEventInHeap(HeapEventType.ImportDocs, {
          action: 'google_docs_import_triage_started',
          type: `${files.length} Docs Fetched from Google`,
        })
      }
      setSearchAgain(false)
    }
  }, [filesLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  const fileIncluded = (f: GoogleDriveFile) => {
    return !!selectedFiles.filter((sf) => sf.id === f.id).length
  }

  const onCheck = (file: GoogleDriveFile) => {
    if (startReview) {
      extractContents([file])
      return
    }

    if (fileIncluded(file)) {
      setSelectedFiles(selectedFiles.filter((f) => f.id !== file.id))
    } else {
      setSelectedFiles(selectedFiles.concat(file))
    }
  }

  const selectAllCurrentFiles = (checked: boolean) => {
    if (!files) return

    setSelectedFiles(checked ? files : [])
  }

  const FileRows = () => {
    if (!filesLoaded) {
      return <LoadingState />
    }

    if (!files?.length) {
      if (searchTerm.current?.value) {
        return (
          <div className={Styles.emptyState}>
            <EmptyState
              text={'No results match this search.'}
              subtext={'Try another search to find a specific file.'}
              cta={
                <Button variant="secondary" size="sm" onPress={clearSearch}>
                  Clear Search
                </Button>
              }
            />
          </div>
        )
      }
      return (
        <div className={Styles.emptyState}>
          <EmptyState text={'No files found.'} subtext={'Try another Google account.'} />
        </div>
      )
    }

    return (
      <div>
        <div className={Styles.tableHeader}>
          <div className={Styles.tableHeaderLeft}>
            {!startReview && (
              <Checkbox aria-label="Select all" onChange={(isSelected) => selectAllCurrentFiles(isSelected)} />
            )}
            <NameIcon />
            <div>Name</div>
          </div>
          <div className={Styles.tableHeaderRight}>
            <div className={Styles.calendarIcon}>
              <CalendarIcon />
            </div>
            <div>Updated</div>
            <ArrowUpIcon />
          </div>
        </div>
        {files.map((file: any) => (
          <ImportFileRow
            key={(file.name + file.modifiedTime).split(' ').join('')}
            file={file}
            selected={fileIncluded(file)}
            onCheck={onCheck}
            selectOne={!!startReview}
          />
        ))}
      </div>
    )
  }

  const extractContents = async (files?: GoogleDriveFile[]) => {
    const chosenFiles = files || selectedFiles
    setIsImporting(true)
    trackEventInHeap(HeapEventType.ImportDocs, {
      action: 'google_docs_import_finished',
      type: `${chosenFiles.length} Docs Imported from Google`,
    })
    const filesToSubmit = [] as File[]
    const promises = chosenFiles.map(async (file) => {
      await gapi.client.drive.files
        .export({
          fileId: file.id,
          mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        })
        .then(function (res) {
          const newFile = convertBytesToFile(res.body, file.name)

          filesToSubmit.push(newFile)
        })
    })

    await Promise.all(promises)
    filesSelected(filesToSubmit)
    setIsImporting(false)
  }

  const switchAccounts = () => {
    google.accounts.id.revoke(email)
    if (searchTerm.current) searchTerm.current.value = ''
    setQueryString(defaultQueryString)
    setSelectedFiles([])
    setReload(true)
  }

  const clearSearch = () => {
    if (searchTerm.current) searchTerm.current.value = ''
    setQueryString(defaultQueryString)
    setSearchAgain(true)
  }

  // if we want to search contents of a file: fullText contains '${searchTerm}'
  const debouncedOnSubmit = debounce((e: React.KeyboardEvent) => {
    e.preventDefault()
    setQueryString(defaultQueryString + ` and name contains '${searchTerm.current?.value}'`)
    setSearchAgain(true)
  }, 250)

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => debouncedOnSubmit(e), [debouncedOnSubmit])

  return (
    <>
      <Modal.Content className={Styles.googleModal}>
        <div className={Styles.searchWrapper}>
          <div className={cx(Styles.searchContents, { [Styles.focused]: searchFocused })}>
            <SearchIcon />
            <input
              ref={searchTerm}
              placeholder="Search files..."
              className={Styles.input}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onKeyDown={handleKeyDown}
              name="searchTerm"
            />
          </div>
        </div>

        <div className={Styles.contents}>{FileRows()}</div>
      </Modal.Content>
      {!startReview && (
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            {email && (
              <div className={Styles.textContainer}>
                <div className={Styles.text}>
                  Connected as <span className={Styles.email}>{email}</span>
                </div>
                <div className={Styles.link} onClick={switchAccounts}>
                  Use a different account
                </div>
              </div>
            )}
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            {isMobile ? (
              <Modal.FooterButton isDisabled={!!isImporting} size="lg" onPress={() => extractContents()}>
                Import
              </Modal.FooterButton>
            ) : (
              <Tooltip label="Select a doc to import" hide={!!selectedFiles.length}>
                <span>
                  <Modal.FooterButton isDisabled={!!isImporting} size="lg" onPress={() => extractContents()}>
                    Import
                  </Modal.FooterButton>
                </span>
              </Tooltip>
            )}
          </Modal.FooterContent>
        </Modal.Footer>
      )}
    </>
  )
}
